import { Protocol } from '../../../models/Entities/Protocols/Protocol';
import { PatientVisit } from '../../../models/Entities/Protocols/PatientVisist';
import { ProtocolPatientStatusDetail } from '../../../models/Entities/Protocols/ProtocolPatientStatusStatistics';
import {
    dateCell,
    ExcelSheet,
    indicatorRows,
    patientsByProtocolSheet,
    percent
} from '../../Contracts/statistics/StatisticsExcel.Functions';
import { formatMonth, PATIENT_VISIT_STATUS_LABEL } from '../../Contracts/statistics/VisitExecution.Functions';
import { statusStage } from '../../Contracts/statistics/PatientStatusStatistics.Functions';
import * as Functions from './ProtocolsPortfolioStatistics.Functions';

/** Hojas de Excel de la cartera de protocolos. Funciones puras: datos entran, filas salen. */

const yesNo = (value: boolean): string => (value ? 'Sí' : 'No');

const distributionSheet = (name: string, items: Functions.CountItem[], total: number): ExcelSheet => ({
    name,
    description: `Protocolos agrupados ${name.toLowerCase()}.`,
    rows: items.map((item) => ({
        Categoría: item.label,
        Protocolos: item.count,
        '% de la cartera': percent(total > 0 ? Math.round((item.count / total) * 1000) / 10 : 0)
    }))
});

/**
 * Composición de la cartera: resumen, las distribuciones completas —sin el corte en
 * 10 barras del gráfico— y la lista de protocolos con sus atributos.
 * Recibe los protocolos que muestra el tablero (los ongoing).
 */
export const compositionSheets = (protocols: Protocol[], summary: Functions.PortfolioSummary): ExcelSheet[] => [
    {
        name: 'Resumen',
        description: 'Cifras generales de la cartera.',
        rows: indicatorRows([
            ['Protocolos ongoing', summary.protocols],
            ['Sponsors', summary.sponsors],
            ['Objetivo de pacientes', summary.objective, 'Suma de los objetivos cargados'],
            ['Pacientes con estado', summary.patients.total],
            ['En selección', summary.patients.selection],
            ['Fallas de selección', summary.patients.screenFailure],
            ['Randomizados', summary.patients.randomized],
            ['% del objetivo randomizado', summary.enrollment === null ? '' : percent(summary.enrollment)],
            ['Protocolos sin pacientes', summary.protocolsWithoutPatients]
        ])
    },
    distributionSheet(
        'Por fase',
        Functions.countBy(protocols, (p) => p.phaseId ?? p.phase?.id, (p) => p.phase?.title),
        protocols.length
    ),
    distributionSheet(
        'Por especialidad',
        Functions.countByLabel(protocols, (p) => p.specialty?.description),
        protocols.length
    ),
    distributionSheet(
        'Por sponsor',
        Functions.countByLabel(protocols, (p) => Functions.sponsorName(p)),
        protocols.length
    ),
    distributionSheet(
        'Por indicación',
        Functions.countByLabel(protocols, (p) => p.indication?.description),
        protocols.length
    ),
    {
        name: 'Protocolos',
        description: 'Lista de protocolos con sus atributos.',
        rows: protocols.map((p) => ({
            'N° de protocolo': p.protocolNumber,
            Título: p.title || p.fullTitle || '',
            Estado: p.status?.title ?? '',
            Fase: p.phase?.title ?? '',
            Especialidad: p.specialty?.description ?? '',
            Indicación: p.indication?.description ?? '',
            Sponsor: Functions.sponsorName(p) ?? '',
            'Objetivo de pacientes': p.patientsObjective || '',
            'En curso': yesNo(Functions.isOpenProtocol(p)),
            Creado: dateCell(p.created),
            Cierre: dateCell(p.closed)
        }))
    }
];

/** Reclutamiento por protocolo, con todas las columnas de la tabla y el título completo. */
export const recruitmentSheets = (rows: Functions.RecruitmentRow[]): ExcelSheet[] => [
    {
        name: 'Reclutamiento',
        description: 'Pacientes por etapa de cada protocolo y avance contra el objetivo.',
        rows: rows.map((row) => ({
            'N° de protocolo': row.protocolNumber,
            Título: row.title,
            Sponsor: row.sponsor ?? '',
            Estado: row.status ?? '',
            'En curso': yesNo(row.isOpen),
            'Pacientes con estado': row.counts.total,
            'En selección': row.counts.selection,
            'Fallas de selección': row.counts.screenFailure,
            Randomizados: row.counts.randomized,
            Objetivo: row.objective || '',
            '% del objetivo': row.progress === null ? '' : percent(row.progress)
        }))
    }
];

const timelineSheets = (countLabel: string, data: Functions.PortfolioTimeline): ExcelSheet[] => [
    {
        name: 'Línea de tiempo',
        description: `${countLabel} por mes, con el acumulado.`,
        rows: data.timeline.points.map((point) => ({
            Mes: formatMonth(point.month),
            Período: point.month,
            [countLabel]: point.count,
            Acumulado: point.cumulative
        }))
    },
    {
        name: 'Por protocolo',
        description: `${countLabel} de cada protocolo, de mayor a menor.`,
        rows: data.byProtocol.map((item) => ({ 'N° de protocolo': item.protocolNumber, [countLabel]: item.count }))
    }
];

const protocolNumberOf = (protocols: Protocol[], protocolId: number, fallback?: string | null): string =>
    protocols.find((p) => p.id === protocolId)?.protocolNumber || fallback || `#${protocolId}`;

/**
 * Reclutamiento en el tiempo: línea de tiempo, reparto por protocolo y los pacientes
 * contados, agrupados por protocolo con corte de control.
 */
export const randomizationTimelineSheets = (
    data: Functions.PortfolioTimeline,
    details: ProtocolPatientStatusDetail[],
    protocols: Protocol[]
): ExcelSheet[] => {
    const randomized = (details ?? [])
        .filter((item) => !!item.randomizationDate || statusStage(item.status) === 'randomized')
        .map((item) => ({ ...item, protocolNumber: protocolNumberOf(protocols, item.protocolId, item.protocolNumber) }));

    return [
        ...timelineSheets('Randomizaciones', data),
        patientsByProtocolSheet(
            randomized,
            'Randomizados por protocolo',
            'Pacientes randomizados agrupados por protocolo, con subtotales.'
        )
    ];
};

/** Visitas en el tiempo: línea de tiempo, reparto por protocolo y todas las visitas registradas. */
export const visitsTimelineSheets = (
    data: Functions.PortfolioTimeline,
    items: Functions.ProtocolVisits[],
    protocols: Protocol[]
): ExcelSheet[] => [
    ...timelineSheets('Visitas completadas', data),
    {
        name: 'Visitas',
        description: 'Todas las visitas registradas de los protocolos de la cartera.',
        rows: (items ?? []).flatMap((item) =>
            (item.visits ?? []).map((visit: PatientVisit) => ({
                Protocolo: protocolNumberOf(protocols, item.protocolId),
                'N° en protocolo': visit.numberInProtocol ?? '',
                Paciente: (visit as any).patientName ?? '',
                Visita: visit.title ?? '',
                Tipo: visit.type ?? '',
                Estado: PATIENT_VISIT_STATUS_LABEL[visit.status] ?? `Estado ${visit.status}`,
                Fecha: dateCell(visit.date),
                Profesional: (visit as any).practitioner ?? ''
            }))
        )
    }
];
